import { NANOSECOND_TIMESTAMP_KEYS } from "./parseTimestampsAsBigInt";

/**
 * A quoted timestamp key, a colon, and an integer token followed by `,` or
 * `}`. Built from `NANOSECOND_TIMESTAMP_KEYS`, the reviver's key set, so this
 * parser and `parseTimestampsAsBigInt` convert the same keys.
 * - A float or exponent literal fails the lookahead: a `.` or `e` follows
 *   the digits.
 * - A quoted value fails the integer token.
 * - The lookbehind rejects a timestamp key name quoted inside another string,
 *   whose opening quote is escaped: in `{"see \"start_time": 5}` the key is
 *   `see "start_time` and its value stays a number.
 */
const TIMESTAMP_INTEGER_PATTERN = new RegExp(
  `(?<!\\\\)"(${[...NANOSECOND_TIMESTAMP_KEYS].join("|")})"\\s*:\\s*(-?\\d+)(?=\\s*[,}])`,
  "g",
);

/**
 * Parses JSON text and converts the platform's nanosecond-epoch timestamp
 * fields to `bigint`, returning the same value as
 * `JSON.parse(text, parseTimestampsAsBigInt)` in about a quarter of the time.
 * A reviver runs for every key of every object: on a 10,000-row events page
 * under Node, `JSON.parse` with the reviver takes several times as long as
 * without one.
 *
 * 1. A regular expression quotes each integer token that follows a timestamp
 *    key, prefixed with a marker built from a UUID generated for this call,
 *    which a payload serialized before the call cannot contain.
 * 2. `JSON.parse` runs with no reviver.
 * 3. One walk over the parsed tree turns each marked string under a timestamp
 *    key into the `bigint` of its digits, and stops once every marked token is
 *    converted. Nothing the expression did not mark is converted.
 *
 * The conversion rules are the reviver's: an integer token under a timestamp
 * key at any depth (a customer's `metadata.start_time` included) becomes a
 * `bigint`; a quoted digit string, a float or exponent literal, `null`,
 * `true`, `false`, objects, and arrays stay as `JSON.parse` returns them.
 */
export function parseJsonWithBigIntTimestamps(text: string): unknown {
  const marker = `bigint:${crypto.randomUUID()}:`;
  let marked = 0;
  const rewritten = text.replace(
    TIMESTAMP_INTEGER_PATTERN,
    (_match, key: string, digits: string) => {
      marked += 1;
      return `"${key}":"${marker}${digits}"`;
    },
  );
  const parsed: unknown = JSON.parse(rewritten);
  convertMarkedStrings(parsed, { marker, remaining: marked });
  return parsed;
}

interface Conversion {
  marker: string;
  /** Marked tokens not yet converted; the walk ends when this reaches zero. */
  remaining: number;
}

function convertMarkedStrings(node: unknown, conversion: Conversion): void {
  if (conversion.remaining === 0 || node === null || typeof node !== "object") {
    return;
  }
  if (Array.isArray(node)) {
    for (const item of node) {
      convertMarkedStrings(item, conversion);
      if (conversion.remaining === 0) {
        return;
      }
    }
    return;
  }
  const record = node as Record<string, unknown>;
  for (const key in record) {
    const value = record[key];
    if (typeof value === "string") {
      if (
        NANOSECOND_TIMESTAMP_KEYS.has(key) &&
        value.startsWith(conversion.marker)
      ) {
        record[key] = BigInt(value.slice(conversion.marker.length));
        conversion.remaining -= 1;
      }
    } else if (typeof value === "object" && value !== null) {
      convertMarkedStrings(value, conversion);
    }
    if (conversion.remaining === 0) {
      return;
    }
  }
}
